/**
 * Canonical visit-note prompts — the prompts the server actually sends.
 *
 * PRESENTATION DECISION (documented here because it is a judgement call):
 *
 * The canonical vocabulary is TWO AXES — format (SOAP | H&P | Quick) x standard
 * (none | MEAT | E&M) — which is NINE prompts, not four. Three ways to present
 * that, and why this one:
 *
 *  - Nine sibling cards. Honest but unusable: each card carries a full prompt
 *    textarea, a model picker and version history, so the page becomes a
 *    ~5,000-line scroll in which the four legacy cards are lost, and the reader
 *    has to infer the axis structure from nine similar names.
 *  - One card plus two dropdowns. Compact, but it hides eight of nine behind a
 *    control, and a prompt you cannot see is a prompt nobody audits. It also
 *    makes "which of these has an admin actually tuned?" unanswerable.
 *  - THIS: a format TAB STRIP, and for the selected format one card per standard.
 *    The axes stay visible as axes; every combination is reachable in one click
 *    and none is dropped; three cards at a time is readable; and the card that
 *    matters most (SOAP / no standard, the default request) is what the page opens
 *    on.
 *
 * The section is rendered ABOVE the legacy cards on purpose. These are the live
 * prompts; the legacy four serve only callers still passing the deprecated
 * `noteType`. Ordering them the other way is what made the original bug possible.
 *
 * Data sources are NOT duplicated here. A canonical shape's gate resolves to the
 * same visit_note_settings row the legacy card already administers
 * (`settingsNoteTypeFor`), so each card names the card that owns its gate rather
 * than offering a second control over one row. H&P is the exception and says so.
 */
import { useState } from "react";

import {
  ALL_DOCUMENTATION_STANDARDS,
  ALL_NOTE_FORMATS,
  canonicalVisitNoteTemplateKey,
  DOCUMENTATION_STANDARD_LABELS,
  NOTE_FORMAT_LABELS,
  settingsNoteTypeForCanonicalShape,
  VISIT_NOTE_TYPE_LABELS,
  type NoteFormat,
} from "../graphql/adminOps";
import { AiPromptTemplateCard } from "./AiPromptTemplateCard";

export interface CanonicalNotePromptSectionProps {
  modelAliases: string[];
}

export function CanonicalNotePromptSection({
  modelAliases,
}: CanonicalNotePromptSectionProps) {
  const [format, setFormat] = useState<NoteFormat>("SOAP");

  return (
    <section data-testid="canonical-section" className="flex flex-col gap-3">
      <div>
        <h2 className="text-sm font-bold m-0">Canonical prompts (in use)</h2>
        <p className="text-xs text-muted-foreground mt-1 mb-0">
          These are the prompts the server sends today. A visit note is a{" "}
          <strong>format</strong> (SOAP, H&amp;P, Quick) plus an optional{" "}
          <strong>documentation standard</strong> (MEAT, E&amp;M), and each
          combination has its own prompt, keyed{" "}
          <code>visit-note.canonical.&lt;format&gt;[.&lt;standard&gt;]</code>.
          Pick a format to see its three prompts.
        </p>
      </div>

      {/* Format tabs */}
      <div
        role="tablist"
        aria-label="Note format"
        className="flex items-center gap-1 rounded-lg border border-border p-1 self-start"
      >
        {ALL_NOTE_FORMATS.map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={f === format}
            data-testid={`format-tab-${f}`}
            onClick={() => setFormat(f)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              f === format
                ? "bg-primary text-primary-foreground"
                : "hover:bg-accent"
            }`}
          >
            {NOTE_FORMAT_LABELS[f]}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-6">
        {ALL_DOCUMENTATION_STANDARDS.map((standard) => {
          const shape = { format, standard };
          const gate = settingsNoteTypeForCanonicalShape(shape);
          return (
            <AiPromptTemplateCard
              key={`${format}-${standard}`}
              templateKey={canonicalVisitNoteTemplateKey(shape)}
              title={`${NOTE_FORMAT_LABELS[format]} · ${DOCUMENTATION_STANDARD_LABELS[standard]}`}
              publishLabel={`${NOTE_FORMAT_LABELS[format]} / ${DOCUMENTATION_STANDARD_LABELS[standard]}`}
              idPrefix={`vnc-${format.toLowerCase()}-${standard.toLowerCase()}`}
              testId={`canonical-card-${format}-${standard}`}
              modelAliases={modelAliases}
              seedMigration="0019_canonical_visit_note_templates.sql"
              subtitle={
                gate ? (
                  <>
                    Data sources for this prompt are the{" "}
                    <strong>{VISIT_NOTE_TYPE_LABELS[gate]}</strong> card&apos;s
                    toggles below — one shared row, not a second copy.
                  </>
                ) : (
                  <>
                    H&amp;P has its own data-source row (<code>hp</code>, seeded
                    by <code>sql/0018</code>), but the admin API does not expose
                    it yet, so it cannot be edited here and every source stays
                    on.
                  </>
                )
              }
            />
          );
        })}
      </div>
    </section>
  );
}

export default CanonicalNotePromptSection;
